import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Bot, Cpu, Webhook, GitBranch, Download, AlertCircle, CheckCircle2, Loader2, Search, Wand2 } from 'lucide-react';
import { haptic } from '../../telegram';
import { marketplaceApi, type DetailListingCard, type RequirementInputs } from '../../api/marketplaceApi';

const KIND_ICONS: Record<string, any> = { agent: Bot, mcp: Cpu, webhook: Webhook, pipeline: GitBranch };
const KIND_LABELS: Record<string, string> = { agent: 'Агент', pipeline: 'Сценарий', mcp: 'MCP', webhook: 'Вебхук' };

type ChatOption = { id: string; title: string; type: string };

const preLine: React.CSSProperties = { whiteSpace: 'pre-line', lineHeight: 1.5 };
const sameHintStyle: React.CSSProperties = { textAlign: 'center', marginTop: 6 };

/*
  «Хочу так же»: готовый сценарий из витрины превращается в текстовое описание
  для конструктора. Пользователь не пишет с нуля, а правит пример под себя —
  это главный мост от витрины к созданию своих сценариев своими словами.
  Описание листинга берём только короткое: длинная инструкция по настройке
  только сбивает оркестратор и пугает пользователя стеной текста в поле ввода.
*/
function buildSeedPrompt(item: DetailListingCard): string {
  const clip = (s: string, max: number) => (s.length > max ? s.slice(0, max).trimEnd() + '…' : s);
  const title = String(item.title || '').trim();
  const summary = String(item.summary || '').trim();
  const lines = [`Хочу автоматизацию как «${title}».`];
  if (summary) lines.push(clip(summary, 400));
  lines.push('Сделай так же, но под меня. Ниже допишу, что нужно поменять:');
  return lines.join('\n');
}
const errStyle: React.CSSProperties = { color: 'var(--rf-red)' };
const chatWrap: React.CSSProperties = { marginTop: 8 };
const chipRow: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 };

// Расписание настраивается выбором дня и времени, а не строкой cron — её пользователи не понимают.
const WEEKDAYS: Array<{ value: string; label: string }> = [
  { value: 'mon', label: 'Пн' },
  { value: 'tue', label: 'Вт' },
  { value: 'wed', label: 'Ср' },
  { value: 'thu', label: 'Чт' },
  { value: 'fri', label: 'Пт' },
  { value: 'sat', label: 'Сб' },
  { value: 'sun', label: 'Вс' },
];

/** Значение хранится строкой вида "mon,wed,fri" — порядок дней всегда календарный. */
function toggleWeekday(current: string, day: string): string {
  const set = current.split(',').map((s) => s.trim()).filter(Boolean);
  const next = set.includes(day) ? set.filter((d) => d !== day) : [...set, day];
  const order = WEEKDAYS.map((d) => d.value);
  return next.sort((a, b) => order.indexOf(a) - order.indexOf(b)).join(',');
}

export function MarketplaceDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [item, setItem] = useState<DetailListingCard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [inputs, setInputs] = useState<RequirementInputs>({});
  const [installing, setInstalling] = useState(false);
  const [installResult, setInstallResult] = useState<{ ok: boolean; msg: string } | null>(null);

  // Helper: "Узнать chat ID"
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);
  const [chatHint, setChatHint] = useState<string | null>(null);
  const [chatOptions, setChatOptions] = useState<ChatOption[]>([]);

  useEffect(() => {
    if (!id) return;
    marketplaceApi.getListing(id)
      .then((d) => {
        setItem(d);
        const initInputs: RequirementInputs = {};
        (d.requirements || []).forEach((r: any) => { if (r.default != null) initInputs[r.ref] = String(r.default); });
        setInputs(initInputs);
        setLoading(false);
      })
      .catch(() => { setError(true); setLoading(false); });
  }, [id]);

  const lookupChats = async () => {
    const token = (inputs['bot_token'] || '').trim();
    setChatError(null); setChatHint(null); setChatOptions([]);
    if (!token) { setChatError('Сначала вставьте токен бота в поле выше.'); return; }
    setChatLoading(true); haptic('light');
    try {
      const res = await marketplaceApi.resolveTelegramChatId(token);
      setChatOptions(res.chats || []);
      if (res.hint) setChatHint(res.hint);
      else if (!res.chats || res.chats.length === 0) setChatHint('Чаты не найдены. Добавьте бота в чат и напишите там сообщение, затем повторите.');
    } catch (e: any) {
      setChatError(e?.message || 'Не удалось определить chat ID');
    } finally { setChatLoading(false); }
  };

  const install = async () => {
    if (!item) return;
    haptic('medium'); setInstalling(true); setInstallResult(null);
    try {
      await marketplaceApi.installListing(item.id, inputs);
      setInstallResult({ ok: true, msg: 'Установлено! Доступно во вкладке «Сценарии».' });
      haptic('heavy');
    } catch (e: any) {
      setInstallResult({ ok: false, msg: e?.message || 'Ошибка установки' });
    } finally { setInstalling(false); }
  };

  if (loading) return (
    <div className="rf-detail">
      <div className="rf-topbar"><button className="rf-back" onClick={() => { haptic('light'); nav(-1 as any); }}><ArrowLeft />Назад</button><div className="rf-topbar-title">Загрузка…</div></div>
      <div className="rf-detail-scroll"><div className="rf-loading"><div className="rf-spin" /></div></div>
    </div>
  );

  if (error || !item) return (
    <div className="rf-detail">
      <div className="rf-topbar"><button className="rf-back" onClick={() => { haptic('light'); nav(-1 as any); }}><ArrowLeft />Назад</button><div className="rf-topbar-title">Не найдено</div></div>
      <div className="rf-detail-scroll"><div className="rf-empty"><div className="rf-empty-ic"><AlertCircle /></div><h3>Не найдено</h3><p>Листинг не существует или удалён</p></div></div>
    </div>
  );

  const Icon = KIND_ICONS[item.kind] || Bot;
  const hasBotToken = (item.requirements || []).some((x: any) => x.ref === 'bot_token');

  return (
    <div className="rf-detail">
      <div className="rf-topbar"><button className="rf-back" onClick={() => { haptic('light'); nav(-1 as any); }}><ArrowLeft />Назад</button><div className="rf-topbar-title">{item.title}</div></div>

      <div className="rf-detail-scroll">
        <div className="rf-card rf-dcard">
          <div className="rf-dcard-head">
            <div className="rf-dcard-art"><Icon /></div>
            <div className="rf-dcard-info">
              <div className="rf-dcard-title">{item.title}</div>
              <div className="rf-dcard-desc">{item.summary}</div>
            </div>
          </div>
          <div className="rf-tags">
            <span className="rf-tag">{item.installs_count} установок</span>
            <span className="rf-tag">{KIND_LABELS[item.kind] || item.kind}</span>
            {item.category && <span className="rf-tag">{item.category}</span>}
            {item.is_system && <span className="rf-tag">Системный</span>}
          </div>
        </div>

        {item.description && item.description !== item.summary && (
          <>
            <div className="rf-seclabel">Как это настроить</div>
            <div className="rf-card rf-cardpad"><div style={preLine}>{item.description}</div></div>
          </>
        )}

        {item.requirements && item.requirements.length > 0 && (
          <>
            <div className="rf-seclabel">Параметры для установки</div>
            {item.requirements.map((r: any) => {
              const isChat = r.ref === 'chat' || r.ref === 'chat_id';
              return (
                <div key={r.ref} className="rf-field">
                  <label>{r.label || r.ref}</label>
                  {r.type === 'time' ? (
                    <input type="time" className="rf-input" value={inputs[r.ref] || ''} onChange={(e) => setInputs({ ...inputs, [r.ref]: e.target.value })} />
                  ) : r.type === 'weekday' ? (
                    <div className="rf-chipgrid">
                      {WEEKDAYS.map((d) => {
                        const on = (inputs[r.ref] || '').split(',').map((s) => s.trim()).includes(d.value);
                        return (
                          <button
                            type="button"
                            key={d.value}
                            className={'rf-chiptog' + (on ? ' on' : '')}
                            onClick={() => { setInputs({ ...inputs, [r.ref]: toggleWeekday(inputs[r.ref] || '', d.value) }); haptic('light'); }}
                          >{d.label}</button>
                        );
                      })}
                    </div>
                  ) : (
                    <input className="rf-input" type={r.type === 'secret' ? 'password' : 'text'} placeholder={r.placeholder || (r.required ? 'Обязательно' : 'Необязательно')} value={inputs[r.ref] || ''} onChange={(e) => setInputs({ ...inputs, [r.ref]: e.target.value })} />
                  )}
                  {r.description && <div className="rf-hint">{r.description}</div>}
                  {isChat && hasBotToken && (
                    <div style={chatWrap}>
                      <button type="button" className="rf-btn ghost sm" disabled={chatLoading} onClick={lookupChats}>
                        {chatLoading ? <Loader2 className="spin" /> : <Search />}Узнать chat ID
                      </button>
                      {chatError && <div className="rf-hint" style={errStyle}>{chatError}</div>}
                      {chatHint && <div className="rf-hint">{chatHint}</div>}
                      {chatOptions.length > 0 && (
                        <div style={chipRow}>
                          {chatOptions.map((c) => (
                            <button type="button" key={c.id} className="rf-btn ghost sm" onClick={() => { setInputs((prev) => ({ ...prev, [r.ref]: String(c.id) })); haptic('light'); }}>
                              {c.title} · {c.type} · {c.id}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}

        <button className="rf-btn primary block rf-mt18" onClick={install} disabled={installing}>
          {installing ? <Loader2 className="spin" /> : <Download />}{installing ? 'Установка…' : 'Установить'}
        </button>

        {installResult && (
          <div className={'rf-result ' + (installResult.ok ? 'ok' : 'err')}>{installResult.ok ? <CheckCircle2 /> : <AlertCircle />}<div>{installResult.msg}</div></div>
        )}

        <button
          className="rf-btn ghost block rf-mt18"
          onClick={() => { haptic('light'); nav('/automation/wizard', { state: { seedPrompt: buildSeedPrompt(item) } }); }}
        >
          <Wand2 />Хочу так же, но по-своему
        </button>
        <div className="rf-hint" style={sameHintStyle}>Откроем конструктор с готовым описанием — допишите своими словами, что поменять</div>
      </div>
    </div>
  );
}
